import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS, MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileStates from 'FileStates';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import staticURL from 'lib/staticURL';
import {
  getMaxAminoAcidCount,
  getModelDisplayName,
  getDefaultStructurePredictionModel,
  getAvailableStructurePredictionModels,
  supportsMultimers,
} from 'model-hub/scientific-models/ScientificModelConstants';
import {RemainingExecutionsAndTenantNameQuery} from 'molbio/aa-sequences/alphafold/__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from 'molbio/aa-sequences/alphafold/__generated/UserVerificationStatusQuery';
import AlphaFoldTabState from 'molbio/aa-sequences/alphafold/AlphaFoldTabState';
import {
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  requestRun,
  USER_VERIFICATION_STATUS,
} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import StickyFooter, {
  STICKY_FOOTER_CONTAINER_STYLES,
  STICKY_FOOTER_CONTENT_STYLES,
} from 'molbio/aa-sequences/alphafold/structure-prediction/StickyFooter';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {CenteredSpinner} from 'ui-library/Spinner';

import ComponentSelector from './ComponentSelector';
import {
  ComponentValidationResult,
  flattenComponentSelectionToComponentIds,
  getComponentValidationResult,
  getValidationErrorMessages,
  SelectedComponents,
} from './ComponentUtils';
import ModelSelector from './ModelSelector';

const navigateToProtein = async (proteinId: string): Promise<void> => {
  const fileState = await FileStates.get().openFolderItem(proteinId, {open: true});
  if (!fileState) {
    return;
  }
  const existingTab = fileState.findTab(AlphaFoldTabState);
  if (existingTab != null) {
    existingTab.set({active: true});
  }
};

const submitStructurePrediction = async (
  inputProteinId: string | null,
  selectedComponents: SelectedComponents,
  selectedModel: string,
  setSubmitting: (submitting: boolean) => void,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery,
  onSubmit?: () => void
): Promise<void> => {
  setSubmitting(true);
  try {
    const {proteinIds, moleculeIds} = flattenComponentSelectionToComponentIds(selectedComponents);
    const primaryProteinId = inputProteinId ?? proteinIds[0];
    if (!primaryProteinId) {
      throw new Error('No primary protein id found');
    }
    await requestRun(
      proteinIds,
      moleculeIds,
      primaryProteinId,
      remainingExecutionsAndTenantNameData,
      selectedModel
    );
    trackEvent(MOLBIO_ANALYTICS_ACTIONS.REQUESTED_ALPHAFOLD_RUN);
    if (inputProteinId == null) {
      await navigateToProtein(primaryProteinId);
    }
    onSubmit?.();
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    notifications.error('Error when submitting structure prediction. Please try again.');
  } finally {
    setSubmitting(false);
  }
};

export interface StructurePredictionFormProps {
  inputProteinId?: string;
  inputAminoAcids?: string;
  inputPermissions?: Permissions;
  startingModel?: string;
  onRunAllModels?: () => Promise<void>;
  onSubmit?: () => void;
  onBack?: () => void;
}

export default function StructurePredictionForm({
  inputProteinId,
  inputAminoAcids,
  inputPermissions,
  startingModel,
  onRunAllModels,
  onSubmit,
  onBack,
}: StructurePredictionFormProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  // If an input model is provided, use it. Otherwise, use the default structure prediction model
  // as the start and allow the user to change the model.
  const showModelSelector = startingModel == null;
  const [selectedModel, setSelectedModel] = useState<string>(
    startingModel ?? getDefaultStructurePredictionModel()
  );
  const needsComponentSelection = inputProteinId == null;
  const remainingExecutionsAndTenantNameQueryResult =
    useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);

  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>(
    !needsComponentSelection
      ? {
          [inputProteinId]: {
            aminoAcids: inputAminoAcids ?? '',
            count: 1,
            numHeavyAtoms: 0,
            entityType: ModelTypeConstants.PROTEIN,
            permissions: {
              readable: inputPermissions?.readable ?? false,
              polymerFeaturesEditable: inputPermissions?.polymerFeaturesEditable ?? false,
            },
          },
        }
      : {}
  );
  const [validationResult, setValidationResult] = useState<ComponentValidationResult | null>(null);
  useEffect(() => {
    const result = getComponentValidationResult(selectedComponents, selectedModel);
    setValidationResult(result);
  }, [selectedComponents, selectedModel]);

  if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.PENDING ||
    userVerificationStatusQueryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  } else if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.ERROR ||
    userVerificationStatusQueryResult.type === QueryResultType.ERROR
  ) {
    notifications.error('Error when loading structure prediction form. Please refresh the page.');
    return <></>;
  }

  const currentUser = userVerificationStatusQueryResult.data.currentUser;
  assert(
    currentUser?.__typename === 'UserNode' || currentUser == null,
    `Non-user should not access this page.`
  );

  let blockingBannerMessage: string | null = null;
  if (
    BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC &&
    !(
      // (A) Valid academic user from a verified institution
      (
        (currentUser?.institution?.verified && currentUser?.verified) ||
        // (B) Valid trial industry user. We hackily use MOLBIO_ALPHAFOLD_IS_ACADEMIC for per-user limits.
        // These users should always be verified before using the tenant, so failing this check is very
        // unexpected, to the point that we don't bother tailoring the banner message.
        (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT && currentUser?.verified)
      )
    )
  ) {
    blockingBannerMessage =
      'Structure prediction is only available to verified academic users from verified institutions.';
  } else if (remainingExecutionsAndTenantNameQueryResult.data.remainingAlphafoldExecutions === 0) {
    blockingBannerMessage = 'You do not have any remaining structure prediction runs.';
  } else if (Object.keys(selectedComponents).length !== 0 && validationResult != null) {
    blockingBannerMessage = getValidationErrorMessages(validationResult).join('\n');
  }
  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        {showModelSelector && onRunAllModels && !blockingBannerMessage && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              hideIcon={true}
              text={`Not sure which model to run? Compare them by running all ${getAvailableStructurePredictionModels().length}.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: submitting ? 'Submitting...' : 'Run All',
                actionOnClick: async () => {
                  if (submitting) {
                    return;
                  }
                  trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CLICKED_STRUCTURE_PREDICTION_RUN_ALL_MODELS, {
                    actionOrigin: ActionOrigin.MODEL_HUB_STRUCTURE_PREDICTION_FORM_PAGE,
                  });
                  setSubmitting(true);
                  try {
                    await onRunAllModels?.();
                  } finally {
                    setSubmitting(false);
                    onSubmit?.();
                  }
                },
                buttonStyle: submitting ? ButtonStyle.TEXT : ButtonStyle.PRIMARY,
                rightAlign: true,
              }}
            />
          </div>
        )}

        <div className={css(styles.contentPadding)}>
          {blockingBannerMessage && (
            <div className={css(styles.largeInlineBannerMargin)}>
              <InlineBanner text={blockingBannerMessage} bannerType={InlineBannerType.WARNING} />
            </div>
          )}
          <div className={css(styles.centered, styles.imgMargin)}>
            <img
              width={255}
              height={175}
              src={staticURL('/static/img/alphafold_example_quality_structure.png')}
            />
          </div>
          <p className={css(styles.largestText, styles.centered)}>Predict 3D structure</p>
          <p className={css(styles.smallerText, styles.grayDarker, styles.centered)}>
            Predict structures of proteins up to {getMaxAminoAcidCount(selectedModel)} residues using&nbsp;
            {getModelDisplayName(selectedModel)}
          </p>
        </div>

        {showModelSelector && (
          <div className={css(styles.centered, styles.modelSelectorContainer)}>
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={(model) => {
                setSelectedModel(model);
              }}
              disabled={false}
              className={css(styles.modelSelector)}
            />
          </div>
        )}

        {(supportsMultimers(selectedModel) || needsComponentSelection) && (
          <ComponentSelector
            selectedComponents={selectedComponents}
            onComponentsChange={setSelectedComponents}
            primaryProteinId={inputProteinId ?? null}
            model={selectedModel}
          />
        )}
      </div>
      <StickyFooter>
        {onBack && (
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            text="Cancel"
            onClick={onBack}
          />
        )}
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text={submitting ? 'Submitting...' : 'Submit'}
          onClick={async () => {
            await submitStructurePrediction(
              inputProteinId ?? null,
              selectedComponents,
              selectedModel,
              setSubmitting,
              remainingExecutionsAndTenantNameQueryResult.data,
              onSubmit
            );
          }}
          disabled={validationResult != null || submitting || blockingBannerMessage != null}
        />
      </StickyFooter>
    </div>
  );
}

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  content: STICKY_FOOTER_CONTENT_STYLES,
  contentPadding: {
    padding: '20px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  title: {
    ...FONT_PROXIMA_17,
    fontWeight: 600,
    color: GRAY_DARKER,
  },
  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  imgMargin: {
    margin: '34px 0px',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  grayDarker: {
    color: GRAY_DARKER,
  },
  modelSelectorContainer: {
    margin: '20px 0px',
  },
  modelSelector: {
    minWidth: '400px',
    textAlign: 'left',
    padding: '0px 16px',
  },
  largeInlineBannerMargin: {
    margin: '18px 11px',
  },
});
